import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// UPDATED 2026-08-05: this used to be a single page — search/browse list
// PLUS the full edit/action panel, the panel appearing inline below
// whatever list was showing. Moved the panel to its own routed page
// (AdminUserDetail.jsx, /admin/users/:pk) since a growing user directory
// meant scrolling past an ever-longer list to reach it. This file is now
// list-only: search + the paginated/sortable directory table, both just
// navigating to that page on row click. Shared bits (dialogs, status
// badge, style tokens) are exported here for AdminUserDetail.jsx to reuse
// rather than duplicating them.

export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';

export const page = {
  // TopBanner sits above this div (sticky, ~88px tall — same figure its
  // own dropdown uses for its offset) but isn't inside it, so a plain
  // `100vh` here always overflowed the viewport by that much, letting the
  // window scroll far enough to slide content under the sticky banner —
  // clamping height (not minHeight) to exactly what's left below the
  // banner, plus `overflow: hidden`, means this div itself can never grow
  // past the visible screen or scroll at all. Whatever needs to scroll
  // (AdminSidebar/`cardScroll` below) scrolls internally instead, so the
  // banner — and everything beside whatever's scrolling — never gets
  // covered.
  height: 'calc(100vh - 88px)',
  overflow: 'hidden',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  // Fixed, not scroll-with-content — without this, `background-size: cover`
  // sizes the image against THIS element's own box, and on content-heavy
  // pages (Hierarchy Management's stat cards + tables push it well past
  // 100vh) that stretches the image tall and pushes most of it below the
  // fold, so scrolling shows a washed-out/misaligned background instead of
  // the actual image. Anchoring it to the viewport instead means only the
  // content scrolls, the backdrop stays put — same as every other page
  // using this shared style.
  backgroundAttachment: 'fixed',
  display: 'flex',
  // 'stretch' (the flex default, spelled out here since it matters) — the
  // sidebar+card row below needs to actually fill this div's full fixed
  // height so ITS children can size themselves off a real 100%, rather
  // than sitting at their own natural content height and letting anything
  // tall silently escape this div's `overflow: hidden` clipping.
  alignItems: 'stretch',
  justifyContent: 'center',
  padding: '20px',
};
// No maxWidth — every admin page using this (via cardScroll below) is a
// data table/panel beside AdminSidebar, not a login-style form, so it
// should fill whatever width the row wrapper gives it rather than leaving
// blank space on either side on a wide monitor (2026-10-27, previously
// capped at 1350px).
export const card = {
  boxSizing: 'border-box',
  background: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '16px',
  padding: '48px 40px',
  width: '100%',
  margin: '20px 0',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};
// The content-card variant every admin page actually renders next to
// AdminSidebar, inside a row that's stretched to `page`'s own full fixed
// height (see AdminSidebar.jsx and each page's own row wrapper) — `height:
// '100%'` here fills that exactly, and scrolls its own overflow
// internally instead of the whole window scrolling and dragging the card
// up behind the sticky banner. (Needed `card` to actually have
// `boxSizing: border-box` above for this to be exact — without it, this
// box's own padding added on top of the 100% instead of eating into it,
// so it could still overflow its share of the row by ~96px.)
export const cardScroll = { ...card, flex: 1, minWidth: 0, margin: 0, height: '100%', overflowY: 'auto' };
export const inp = { width: '100%', padding: '10px 14px', border: '1.5px solid #D3D1C7', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' };
export const lbl = { display: 'block', fontSize: '13px', fontWeight: '500', color: '#5F5E5A', marginBottom: '5px' };
export const btn = { padding: '10px 20px', background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' };
export const btnDisabled = { ...btn, opacity: 0.6, cursor: 'not-allowed' };
export const btnSecondary = { ...btn, background: 'none', border: '1px solid #009FE3', color: '#009FE3' };
export const RESEND_COOLDOWN_SECONDS = 30;

export const modalOverlay = {
  position: 'fixed', inset: 0, background: 'rgba(20, 24, 33, 0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '20px', zIndex: 1000,
};
export const modalCard = {
  background: '#fff', borderRadius: '14px', padding: '28px',
  width: '100%', maxWidth: '380px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};

// Shared, custom-styled stand-in for window.confirm() — used by every
// destructive/consequential action (remove email/phone, activate/
// deactivate, reset password) so they all look and behave consistently,
// instead of the browser's own unstyled native dialog.
export function ConfirmDialog({ dialog, onCancel }) {
  if (!dialog) return null;
  return (
    <div style={modalOverlay} onClick={onCancel}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 10px' }}>
          {dialog.title}
        </h3>
        <p style={{ fontSize: '14px', color: '#5F5E5A', margin: '0 0 24px', lineHeight: 1.5 }}>
          {dialog.message}
        </p>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{ ...btnSecondary, flex: 1, borderColor: '#D3D1C7', color: '#666' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={dialog.onConfirm}
            style={{
              ...btn, flex: 1,
              background: dialog.danger ? '#B3261E' : btn.background,
            }}
          >
            {dialog.confirmLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

// is_active:false alone doesn't say why — an admin turned the account off,
// or the person just hasn't finished registration yet (TOTP is mandatory
// and last, so its absence means the latter). registrationStatus (from
// /search, /directory, or the single-user GET) makes that explicit.
// Admin-only distinction — end users never see this wording.
export function statusBadge(registrationStatus) {
  if (registrationStatus === 'incomplete') {
    return { label: 'Registration Incomplete', color: '#8A6D00', background: '#FFF6DA' };
  }
  // TOTP is done, but they never submitted /complete-profile — distinct
  // from 'incomplete' (TOTP never confirmed) so admins can tell which
  // stage someone is actually stuck at.
  if (registrationStatus === 'profile_incomplete') {
    return { label: 'Profile Incomplete', color: '#8A6D00', background: '#FFF6DA' };
  }
  if (registrationStatus === 'rejected') {
    return { label: 'Rejected', color: '#B3261E', background: '#FDECEC' };
  }
  if (registrationStatus === 'deactivated') {
    return { label: 'Deactivated', color: '#B3261E', background: '#FDECEC' };
  }
  return null;
}

// A click-to-navigate row still fires a 'click' event even when the
// "click" was actually the mouseup at the end of a text-selection drag —
// browsers don't distinguish "pressed and released in place" from
// "pressed, dragged to select text, released" at the click-event level.
// Checked live: once a row got its own onClick back (so plain text cells
// are selectable/copyable again — see DirectoryTable's own comment), that
// made selecting a phone number or email ALSO navigate into the user's
// detail page the moment the mouse was released, mid-selection. A
// non-empty window.getSelection() at click time reliably means "this was
// a drag that selected text," not a genuine click — a plain mousedown
// elsewhere already collapses any prior selection before a real click's
// own mouseup fires, so this doesn't false-trigger on stale selection
// from somewhere else on the page.
export function navigateUnlessTextSelected(navigate, path) {
  return () => {
    const selection = window.getSelection();
    if (selection && selection.toString().length > 0) return;
    navigate(path);
  };
}

// Shared tab bar between this page and AdminGroups.jsx (the "User Groups"
// tab requested alongside this directory) — both pages render it so
// switching feels like one section, even though each tab is its own route
// rather than client-side-only state (simpler: each tab keeps its own
// data-fetching/pagination concerns fully separate).
export function DirectoryTabs({ active }) {
  const tabs = [
    { key: 'users', label: 'User Directory', path: '/admin/users' },
    { key: 'groups', label: 'User Groups', path: '/admin/groups' },
  ];
  return (
    <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid #ddd' }}>
      {tabs.map((t) => (
        <Link
          key={t.key}
          to={t.path}
          style={{
            display: 'inline-block',
            padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer',
            fontSize: '14px', fontWeight: active === t.key ? '700' : '500',
            color: active === t.key ? '#009FE3' : '#5F5E5A', textDecoration: 'none',
            borderBottom: active === t.key ? '2px solid #009FE3' : '2px solid transparent',
            marginBottom: '-1px',
          }}
        >
          {t.label}
        </Link>
      ))}
    </div>
  );
}

// Datalist-backed free-text filter (2026-11-02) — a real search box, not
// a strict picker: typing live narrows results via the backend's own
// substring match (adminUsers.js's /directory), and picking a suggestion
// from the native dropdown just fills the same box, no separate "select"
// step. Debounced (300ms) before committing to the parent's filter state
// — every change re-triggers a full backend scan (fetchAllDirectoryUsers),
// not a free client-side filter, same reasoning the page's own free-text
// search box above already debounces for.
function FilterSearchInput({ value, onChange, options, placeholder, listId }) {
  const [draft, setDraft] = useState(value);
  // Keeps this box in sync when the filter is cleared/reset from outside
  // (Clear filters button) rather than by typing here.
  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => {
    const handle = setTimeout(() => {
      if (draft !== value) onChange(draft);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);
  return (
    <>
      <input
        style={{ ...inp, width: '160px' }}
        type="text"
        list={listId}
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <datalist id={listId}>
        {options.map((o) => <option key={o} value={o} />)}
      </datalist>
    </>
  );
}

// Widths kept narrow enough that all 8 columns fit within the card's own
// width without horizontal scrolling on a normal desktop screen.
const DIRECTORY_COLUMNS = [
  { key: 'username', label: 'Username', sortable: true, width: '150px' },
  { key: 'phone', label: 'Phone', sortable: false, width: '110px' },
  { key: 'email', label: 'Email', sortable: true, width: '200px' },
  { key: 'designation', label: 'Designation', sortable: true, width: '110px' },
  { key: 'accountType', label: 'Account Type', sortable: true, width: '130px' },
  { key: 'office', label: 'Working Place', sortable: true, width: '150px' },
  { key: 'status', label: 'Status', sortable: true, width: '130px' },
  { key: 'dateJoined', label: 'Account Created', sortable: true, width: '180px' },
];

// Fixed enum (unlike designation/accountType/zone/etc., which are
// free-form and come from the backend's own facets) — matches
// statusBadge()'s own possible registrationStatus values + labels exactly,
// so the filter dropdown can't drift from what the Status column shows.
const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'incomplete', label: 'Registration Incomplete' },
  { value: 'profile_incomplete', label: 'Profile Incomplete' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'deactivated', label: 'Deactivated' },
];

const th = {
  textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: '700',
  color: '#5F5E5A', borderBottom: '2px solid #eee', whiteSpace: 'nowrap',
};
const td = {
  padding: '10px 12px', fontSize: '13px', borderBottom: '1px solid #f0f0f0',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
};
// Browse-everyone view — default for this page, replaced by the plain
// results list below whenever a search is actively narrowing things down
// (see AdminUsers' searchMode). Selecting a row navigates to
// AdminUserDetail.jsx (/admin/users/:pk) instead of expanding inline.
function DirectoryTable({ users, loading, total, pageNum, setPageNum, pageSize, setPageSize, sortBy, sortDir, onSort }) {
  const navigate = useNavigate();
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'auto', flex: 1, minHeight: 0 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ position: 'sticky', top: 0, background: '#fafafa' }}>
              {DIRECTORY_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  style={{ ...th, width: col.width, cursor: col.sortable ? 'pointer' : 'default' }}
                  onClick={() => col.sortable && onSort(col.key)}
                >
                  {col.label}
                  {col.sortable && sortBy === col.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const badge = statusBadge(u.registrationStatus);
              const rowPath = `/admin/users/${u.pk}`;
              return (
                <tr
                  key={u.pk}
                  onClick={navigateUnlessTextSelected(navigate, rowPath)}
                  className="admin-row-hover"
                  style={{ cursor: 'pointer', background: '#fff' }}
                >
                  {/* CHANGED (2026-11-02): only Username is a real <Link> —
                      right-click/middle-click "open in new tab" works here
                      specifically, the one place you'd expect it. Every
                      other cell went back to plain text (row's own onClick
                      above still navigates on a normal click) after
                      confirming live that wrapping the WHOLE row in links
                      made every value impossible to select/copy — a real
                      browser limitation (a click can't be hit-tested as
                      both "a link" and "plain selectable text" at once),
                      not a CSS bug fixable with draggable/-webkit-user-drag. */}
                  <td style={{ ...td, fontWeight: '600' }} onClick={(e) => e.stopPropagation()}>
                    <Link to={rowPath}>{u.username}</Link>
                  </td>
                  <td style={td}>{u.phone || '—'}</td>
                  <td style={td}>
                    {u.email || (u.pendingEmail
                      ? <span title="Given at invite time, not yet confirmed via the claim link">{u.pendingEmail} <span style={{ color: '#999', fontStyle: 'italic' }}>(pending)</span></span>
                      : '—')}
                  </td>
                  <td style={td}>{u.designation || '—'}</td>
                  <td style={td}>{u.accountType || '—'}</td>
                  <td style={td}>{u.office || '—'}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                      {badge ? (
                        <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px', color: badge.color, background: badge.background }}>
                          {badge.label}
                        </span>
                      ) : (
                        <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px', color: '#0F6E56', background: '#E9F7EF' }}>
                          Active
                        </span>
                      )}
                      {u.needsApproval && (
                        <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px', color: '#B3261E', background: '#FDECEC' }}>
                          Approval
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={td}>{u.dateJoined ? new Date(u.dateJoined).toLocaleString() : '—'}</td>
                </tr>
              );
            })}
            {!loading && users.length === 0 && (
              <tr>
                <td style={{ ...td, color: '#666' }} colSpan={DIRECTORY_COLUMNS.length}>
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap', gap: '10px', flexShrink: 0 }}>
        <div style={{ fontSize: '13px', color: '#666' }}>
          {loading ? 'Loading…' : `${total} user${total === 1 ? '' : 's'}`}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <label style={{ fontSize: '13px', color: '#666', display: 'flex', alignItems: 'center', gap: '6px' }}>
            Show
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPageNum(1); }}
              style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #D3D1C7' }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            per page
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={() => setPageNum((p) => Math.max(1, p - 1))}
              disabled={pageNum <= 1}
              style={{ ...btnSecondary, width: 'auto', marginTop: 0, padding: '6px 12px', opacity: pageNum <= 1 ? 0.5 : 1, cursor: pageNum <= 1 ? 'not-allowed' : 'pointer' }}
            >
              ← Prev
            </button>
            <span style={{ fontSize: '13px', color: '#666' }}>Page {pageNum} of {totalPages}</span>
            <button
              type="button"
              onClick={() => setPageNum((p) => Math.min(totalPages, p + 1))}
              disabled={pageNum >= totalPages}
              style={{ ...btnSecondary, width: 'auto', marginTop: 0, padding: '6px 12px', opacity: pageNum >= totalPages ? 0.5 : 1, cursor: pageNum >= totalPages ? 'not-allowed' : 'pointer' }}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Deletion is permanent (unlike Activate/Deactivate, which ConfirmDialog
// already handles) — requires typing the exact username, not just a
// click, before the Delete button enables. Separate from ConfirmDialog
// since it needs its own input state; same visual language otherwise.
export function DeleteUserDialog({ dialog, onCancel }) {
  const [typed, setTyped] = useState('');

  useEffect(() => {
    setTyped('');
  }, [dialog]);

  if (!dialog) return null;
  const matches = typed === dialog.username;

  return (
    <div style={modalOverlay} onClick={onCancel}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 10px' }}>
          Delete Account
        </h3>
        <p style={{ fontSize: '14px', color: '#5F5E5A', margin: '0 0 16px', lineHeight: 1.5 }}>
          This permanently deletes <strong>{dialog.username}</strong>'s account. This cannot be undone.
          Type <strong>{dialog.username}</strong> below to confirm.
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={dialog.username}
          autoFocus
          style={{ ...inp, marginBottom: '20px' }}
        />
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{ ...btnSecondary, flex: 1, borderColor: '#D3D1C7', color: '#666' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={dialog.onConfirm}
            disabled={!matches}
            style={{
              ...btn, flex: 1, background: '#B3261E',
              opacity: matches ? 1 : 0.5,
              cursor: matches ? 'pointer' : 'not-allowed',
            }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const navigate = useNavigate();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  // /search caps what it actually returns at SEARCH_RESULTS_CAP (200) but
  // always reports the real pre-cap match count + whether it truncated —
  // the UI previously threw both away and only kept `results`, so a count
  // footer had nothing to show and a >200-match search silently looked
  // complete. Kept alongside `results`, not derived from its length.
  const [resultsTotal, setResultsTotal] = useState(0);
  const [resultsTruncated, setResultsTruncated] = useState(false);
  const [searching, setSearching] = useState(false);
  // Directory browser (default view) vs search results (override, shown
  // once a search is actually run).
  const [searchMode, setSearchMode] = useState(false);
  const [directoryUsers, setDirectoryUsers] = useState([]);
  const [directoryTotal, setDirectoryTotal] = useState(0);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  // Named pageNum, not page — this file has a module-level `page` (the
  // outer style object used as <div style={page}>, same convention as
  // every other page component, and exported for AdminUserDetail.jsx to
  // reuse). A same-named state variable here would shadow it within this
  // component's scope, silently turning `style={page}` into `style={1}` —
  // a number, not an object — which crashes on render.
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortBy, setSortBy] = useState('username');
  const [sortDir, setSortDir] = useState('asc');
  const [exporting, setExporting] = useState(false);

  // Designation/Account Type/Zone/Circle/Division/Section/Status filters
  // (2026-11-02) — only apply to the directory browse view, not search
  // mode, matching /directory's own backend scope. `facets` holds the
  // distinct values for each dropdown's options, returned alongside
  // /directory's own results (computed from the FULL unfiltered set
  // server-side, so picking one filter doesn't shrink what the OTHER
  // dropdowns offer).
  const [designation, setDesignation] = useState('');
  const [accountType, setAccountType] = useState('');
  const [zone, setZone] = useState('');
  const [circle, setCircle] = useState('');
  const [division, setDivision] = useState('');
  const [subdivision, setSubdivision] = useState('');
  const [section, setSection] = useState('');
  const [status, setStatus] = useState('');
  const [facets, setFacets] = useState({ designations: [], accountTypes: [], zones: [], circles: [], divisions: [], subdivisions: [], sections: [] });
  const filtersActive = !!(designation || accountType || zone || circle || division || subdivision || section || status);

  const [error, setError] = useState(null);

  function directoryFilterParams() {
    const params = new URLSearchParams({ sortBy, sortDir });
    if (designation) params.set('designation', designation);
    if (accountType) params.set('accountType', accountType);
    if (zone) params.set('zone', zone);
    if (circle) params.set('circle', circle);
    if (division) params.set('division', division);
    if (subdivision) params.set('subdivision', subdivision);
    if (section) params.set('section', section);
    if (status) params.set('status', status);
    return params;
  }

  async function fetchDirectory() {
    setDirectoryLoading(true);
    setError(null);
    try {
      const params = directoryFilterParams();
      params.set('page', pageNum);
      params.set('pageSize', pageSize);
      const res = await fetch(`${API_BASE}/admin/users/directory?${params.toString()}`, { credentials: 'include' });
      if (res.status === 403) {
        setError('You are not authorized to access this page.');
        return;
      }
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();
      setDirectoryUsers(data.results || []);
      setDirectoryTotal(data.total || 0);
      if (data.facets) setFacets(data.facets);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setDirectoryLoading(false);
    }
  }

  // Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
  // header comment) — no more synchronous "is there a token" pre-check;
  // fetchDirectory()'s own 401/403 handling already covers both cases.
  useEffect(() => {
    if (searchMode) return;
    fetchDirectory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchMode, pageNum, pageSize, sortBy, sortDir, designation, accountType, zone, circle, division, subdivision, section, status]);

  // Changing a filter while sitting on page 3 of the old result set would
  // otherwise silently show an empty/wrong page — reset to page 1 instead,
  // same as changing pageSize already does.
  function setFilter(setter) {
    return (val) => { setter(val); setPageNum(1); };
  }

  function clearFilters() {
    setDesignation(''); setAccountType(''); setZone(''); setCircle('');
    setDivision(''); setSubdivision(''); setSection(''); setStatus('');
    setPageNum(1);
  }

  function handleSort(field) {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPageNum(1);
  }

  function clearSearch() {
    setQuery('');
    setResults([]);
    setResultsTotal(0);
    setResultsTruncated(false);
    setSearchMode(false);
    setPageNum(1);
  }

  // Live search as you type — debounced (300ms, so normal typing speed
  // doesn't fire a request per keystroke) and self-cancelling: an
  // AbortController per attempt means a slow response for an earlier,
  // now-stale query can never land after and overwrite a newer one's
  // results (a real race once search is fired on every keystroke instead
  // of one explicit submit at a time). Deleting back down to an empty box
  // exits search mode immediately, no debounce wait — same effect as
  // clicking Clear.
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      if (searchMode) {
        setSearchMode(false);
        setResults([]);
        setResultsTotal(0);
        setResultsTruncated(false);
        setPageNum(1);
      }
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const params = directoryFilterParams();
        params.set('q', trimmed);
        const res = await fetch(`${API_BASE}/admin/users/search?${params.toString()}`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (res.status === 403) {
          setError('You are not authorized to access this page.');
          setResults([]);
          setResultsTotal(0);
          setResultsTruncated(false);
          return;
        }
        if (res.status === 401) {
          navigate('/', { state: { sessionExpired: true } });
          return;
        }
        const data = await res.json();
        setResults(data.results || []);
        setResultsTotal(data.total ?? (data.results || []).length);
        setResultsTruncated(!!data.truncated);
        setSearchMode(true);
      } catch (e) {
        if (e.name !== 'AbortError') {
          setError('Could not reach the identity service.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setSearching(false);
        }
      }
    }, 300);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
    // Filters are intentionally included here (not just `query`) — the
    // 2026-11-03 fix making the filter bar stay visible during search
    // means changing a filter while search results are showing must
    // re-run the search with the new filter set, same as it already
    // re-runs fetchDirectory() in the non-search view (see that effect's
    // own deps just above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, designation, accountType, zone, circle, division, subdivision, section, status]);

  // Kept for the Enter-key/explicit-click path — fires immediately rather
  // than waiting out the debounce above. Harmless if both end up firing
  // (e.g. Enter pressed right as the debounced one was about to go) —
  // same query, same result, the AbortController above just cancels
  // whichever was still pending.
  async function handleSearch(e) {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    setError(null);
    try {
      const params = directoryFilterParams();
      params.set('q', query.trim());
      const res = await fetch(`${API_BASE}/admin/users/search?${params.toString()}`, {
        credentials: 'include',
      });
      if (res.status === 403) {
        setError('You are not authorized to access this page.');
        setResults([]);
        setResultsTotal(0);
        setResultsTruncated(false);
        return;
      }
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();
      setResults(data.results || []);
      setResultsTotal(data.total ?? (data.results || []).length);
      setResultsTruncated(!!data.truncated);
      setSearchMode(true);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setSearching(false);
    }
  }


  function csvEscape(value) {
    const str = String(value ?? '');
    return /[",\n]/.test(str) ? '"' + str.replace(/"/g, '""') + '"' : str;
  }

  // Full, unpaginated CSV of the directory — reuses /export (same
  // full-scan + sort as the table's own /directory call, just without the
  // pagination slice) so admins get everything, not just whatever page
  // they happen to be on. Status text built with the SAME statusBadge()
  // the table renders with, so the CSV never drifts from what's on screen.
  async function handleExport() {
    setExporting(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/users/export?${directoryFilterParams().toString()}`, {
        credentials: 'include',
      });
      if (res.status === 403) {
        setError('You are not authorized to access this page.');
        return;
      }
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      const data = await res.json();
      const rows = data.results || [];
      const headers = ['Username', 'Phone', 'Email', 'Designation', 'Account Type', 'Working Place', 'Status', 'Account Created'];
      const lines = [headers.map(csvEscape).join(',')];
      for (const u of rows) {
        const badge = statusBadge(u.registrationStatus);
        let status = badge ? badge.label : 'Active';
        if (u.needsApproval) status += ' (Approval)';
        lines.push([
          u.username,
          u.phone || '',
          u.email || (u.pendingEmail ? `${u.pendingEmail} (pending)` : ''),
          u.designation || '',
          u.accountType || '',
          u.office || '',
          status,
          u.dateJoined ? new Date(u.dateJoined).toLocaleString() : '',
        ].map(csvEscape).join(','));
      }
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-directory-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
      <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
      <AdminSidebar active="users" />
      <div style={{ ...cardScroll, display: 'flex', flexDirection: 'column' }}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 4px', flexShrink: 0 }}>
          User Directory
        </h1>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '4px', flexShrink: 0 }}>
          Click a user to view full details and manage their account.
        </p>

        <div style={{ flexShrink: 0 }}>
          <DirectoryTabs active="users" />
        </div>

        {error && (
          <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px', flexShrink: 0 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexShrink: 0 }}>
          <input
            style={inp}
            type="text"
            placeholder="Search by username, email, or phone"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button type="submit" disabled={searching} style={searching ? btnDisabled : btn}>
            {searching ? 'Searching…' : 'Search'}
          </button>
          {searchMode && (
            <button type="button" onClick={clearSearch} style={{ ...btnSecondary, width: 'auto', marginTop: 0 }}>
              Clear
            </button>
          )}
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            style={{ ...btnSecondary, width: 'auto', marginTop: 0, marginLeft: 'auto', opacity: exporting ? 0.6 : 1, cursor: exporting ? 'not-allowed' : 'pointer' }}
          >
            {exporting ? 'Exporting…' : '⬇ Export CSV'}
          </button>
        </form>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', marginBottom: '20px', flexShrink: 0 }}>
          <FilterSearchInput listId="filter-designation" placeholder="Designation" options={facets.designations} value={designation} onChange={setFilter(setDesignation)} />
          <FilterSearchInput listId="filter-accountType" placeholder="Account type" options={facets.accountTypes} value={accountType} onChange={setFilter(setAccountType)} />
          <FilterSearchInput listId="filter-zone" placeholder="Zone" options={facets.zones} value={zone} onChange={setFilter(setZone)} />
          <FilterSearchInput listId="filter-circle" placeholder="Circle" options={facets.circles} value={circle} onChange={setFilter(setCircle)} />
          <FilterSearchInput listId="filter-division" placeholder="Division" options={facets.divisions} value={division} onChange={setFilter(setDivision)} />
          <FilterSearchInput listId="filter-subdivision" placeholder="Sub-Division" options={facets.subdivisions} value={subdivision} onChange={setFilter(setSubdivision)} />
          <FilterSearchInput listId="filter-section" placeholder="Section" options={facets.sections} value={section} onChange={setFilter(setSection)} />
          <select style={{ ...inp, width: 'auto' }} value={status} onChange={(e) => setFilter(setStatus)(e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          {filtersActive && (
            <button type="button" onClick={clearFilters} style={{ ...btnSecondary, width: 'auto', marginTop: 0, background: 'none', border: '1px solid #ccc', color: '#5F5E5A' }}>
              Clear filters
            </button>
          )}
        </div>

        {searchMode ? (
          <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div style={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'auto', flex: 1, minHeight: 0 }}>
              {results.length === 0 && !searching && (
                <div style={{ padding: '14px 16px', color: '#666', fontSize: '13px' }}>
                  No users found.
                </div>
              )}
              {results.map((u) => {
                const badge = statusBadge(u.registrationStatus);
                const resultPath = `/admin/users/${u.pk}`;
                return (
                  <div
                    key={u.pk}
                    onClick={navigateUnlessTextSelected(navigate, resultPath)}
                    className="admin-row-hover"
                    style={{
                      padding: '12px 16px', cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      background: '#fff',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: '600', fontSize: '14px' }} onClick={(e) => e.stopPropagation()}>
                        <Link to={resultPath}>{u.username}</Link>
                      </span>
                      {badge && (
                        <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px', color: badge.color, background: badge.background }}>
                          {badge.label}
                        </span>
                      )}
                      {u.needsApproval && (
                        <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '10px', color: '#B3261E', background: '#FDECEC' }}>
                          Approval
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {u.email || (u.pendingEmail ? `${u.pendingEmail} (pending)` : 'no email')} · {u.phone || 'no phone'}
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ fontSize: '13px', color: '#666', marginTop: '12px', flexShrink: 0 }}>
              {searching
                ? 'Searching…'
                : `${resultsTotal} result${resultsTotal === 1 ? '' : 's'}`}
              {resultsTruncated && !searching && (
                <span> — showing the first {results.length}. Narrow your search or filters to see the rest.</span>
              )}
            </div>
          </div>
        ) : (
          <DirectoryTable
            users={directoryUsers}
            loading={directoryLoading}
            total={directoryTotal}
            pageNum={pageNum}
            setPageNum={setPageNum}
            pageSize={pageSize}
            setPageSize={setPageSize}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
          />
        )}
      </div>
      </div>
      </div>
    </>
  );
}
